/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { view } from '@risingstack/react-easy-state';
import { Modal, Button, Loader, Message } from 'ui';
import { useModal } from 'framework/components/ui/Modal';
import { prefixClassName } from 'framework/components/ui/_conf';
import ApplicationMonitoring from 'framework/applicationMonitoring/ApplicationMonitoring';
import { useLocation } from 'react-router-dom';
import { FILEMANAGER_MODAL, missingDataWarning, FILEMANAGER_DUPLICATE_FILE_NAME_MODAL } from './constants';
import { FileManagerProps, ExplorerMode, MissingData, FileFolderType } from './types';
import { FileManagerUpload, FileManagerExplorerView, BulkActionsBar } from './FileManagerComponents';

import './FileManager.scss';
import { handleMultipleFileDelete, useExplorerFileManager, useRootFileManager } from './useInternalFileManager';
import { useFileManagerContext } from './FileManagerContext';
import { FileManagerContextProvider } from './FileManagerContextProvider';
import { FileManagerFileDuplicateModal } from './FileManagerFileDuplicateModal';
import { useFileManagerProvider } from './useFileManager';
import { useFeatureIsOn } from 'growthbook/useFeatureIsOn';
import { FileManagerMoveFileModal } from './FileManagerMoveFileModal';

const clxDamTranslation = 'modules.contentmanagement.dam';
const cssClass = prefixClassName('file-manager');

export const shouldShowBulkActionsBar = (
  isBulkActionsEnabled: boolean,
  selectedFilesCount: number,
  displayMode: ExplorerMode
): boolean => isBulkActionsEnabled && selectedFilesCount > 0 && displayMode === ExplorerMode.EXPLORE;

export const determineMovableFile = (previewableFile: any) => previewableFile ?? undefined;

export const determineMovableFiles = (
  isBulkActionsEnabled: boolean,
  selectedFilesCount: number,
  selectedFiles: any[],
  previewableFile: any
) => (!previewableFile && isBulkActionsEnabled && selectedFilesCount > 0 ? selectedFiles : undefined);

export const shouldShowExplorerView = (displayMode: ExplorerMode): boolean => displayMode === ExplorerMode.EXPLORE;

export const shouldShowUploadView = (displayMode: ExplorerMode): boolean => displayMode === ExplorerMode.UPLOAD;

export const hasParentDirectoryError = (displayMode: ExplorerMode, missingData?: MissingData): boolean =>
  displayMode === ExplorerMode.EXPLORE && missingData === MissingData.PARENT_DIRECTORY;

export const shouldShowUploadFooter = (displayMode: ExplorerMode): boolean => displayMode === ExplorerMode.UPLOAD;

export const hasNewFileName = (newFileName?: string): boolean => !!newFileName;
export const hasErrorFileName = (res: any): boolean => !!res?.fileName;
export const hasSelectedFilesForBulkAction = (selectedFiles: any[]): boolean => selectedFiles.length > 0;
export const isUploadMode = (mode: ExplorerMode): boolean => mode === ExplorerMode.UPLOAD;
export const hasUploadCallback = (onFilesUploaded?: any): boolean => !!onFilesUploaded;
export const hasUploadResult = (res: any): boolean => !!res;
export const hasDismissCallback = (onDismissModal?: any): boolean => !!onDismissModal;
export const hasFormatSetter = (setRequiredFilesFormat?: any): boolean => !!setRequiredFilesFormat;
export const isStoreInitializingInFileManager = (store: any): boolean => !!store?.initializing;

const FileManagerFn = view(
  ({
    mode,
    explorerModalName = FILEMANAGER_MODAL,
    fileSelectable = true,
    onDismissModal,
    onFilesUploaded,
    onFileSelected,
    trackEventProps,
    requiredFilesFormat,
  }: FileManagerProps) => {
    const {
      displayMode,
      missingData,
      uploadableFiles,
      setDisplayMode,
      closeRootModal,
      setDestinationFolder,
      setUploadableFiles,
      handleClearMissingData,
      handleUploadFile,
      handleDeleteFile,
    } = useRootFileManager({ mode, explorerModalName });

    const {
      store,
      previewableFile,
      openMoveModal,
      handleFileDelete,
      setPreviewableFile,
      handleExplorerRowClick,
      handleActiveFolderDelete,
      refreshActiveFolder,
    } = useExplorerFileManager({ explorerModalName });

    const { handleOpenFolderModal, selectedFiles, clearSelectedFiles, bulkAction, setBulkAction, setRefreshTrigger, prompDelete } =
      useFileManagerContext();

    const isBulkActionsEnabled = useFeatureIsOn('admin-dam-search-and-bulk-actions');

    const { pathname } = useLocation();

    const { setRequiredFilesFormat } = useFileManagerProvider();
    const { openModal: openOverwriteConfirmModal, closeModal: closeOverwriteConfirmModal } = useModal(
      FILEMANAGER_DUPLICATE_FILE_NAME_MODAL
    );

    const toggleDisplayMode = (_mode: ExplorerMode) => () => {
      ApplicationMonitoring.trackEvent(
        trackEventProps?.name || pathname,
        trackEventProps?.value || 'Toggle explorer mode',
        trackEventProps?.label || _mode
      );
      setDisplayMode(_mode);
      if (isUploadMode(_mode)) {
        setPreviewableFile(undefined);
      }
    };

    const handleOnUpload = (overwrite: boolean = false, newFileName?: string) => {
      if (hasNewFileName(newFileName)) {
        setUploadableFiles([{ ...uploadableFiles[0], name: newFileName, size: uploadableFiles[0].size }]);
      }
      handleUploadFile(overwrite, newFileName).then((res) => {
        if (hasErrorFileName(res)) {
          openOverwriteConfirmModal();
        } else if (hasUploadResult(res) && hasUploadCallback(onFilesUploaded)) {
          onFilesUploaded([res]);
        }
      });
    };

    const onBulkActionSuccess = () => {
      clearSelectedFiles();
      refreshActiveFolder();
      // Trigger grid view refresh
      setRefreshTrigger?.((prev) => prev + 1);
    };

    const handleBulkMove = () => {
      if (hasSelectedFilesForBulkAction(selectedFiles)) {
        setPreviewableFile(undefined); // Clear single file selection for bulk move
        openMoveModal();
      }
    };

    const handleBulkDelete = () => {
      if (hasSelectedFilesForBulkAction(selectedFiles)) {
        setPreviewableFile(undefined); // Clear single file selection for bulk delete
        prompDelete(FileFolderType.FILE, () => handleMultipleFileDelete(selectedFiles, () => onBulkActionSuccess()))();
      }
    };

    let footerOptions = {};
    if (hasParentDirectoryError(displayMode, missingData)) {
      footerOptions = {
        footerComponent: () => <Message caption={missingDataWarning[MissingData.PARENT_DIRECTORY]} type="error" />,
      };
    } else if (shouldShowUploadFooter(displayMode)) {
      footerOptions = {
        footerComponent: () => (
          <ModalFooterButtons missingData={missingData} onCancel={closeRootModal} onUpload={handleOnUpload} />
        ),
      };
    }

    return (
      <>
        <Modal
          name={explorerModalName}
          caption="modules.contentmanagement.fileManager"
          onDismiss={() => {
            if (hasDismissCallback(onDismissModal)) onDismissModal();
            if (hasFormatSetter(setRequiredFilesFormat)) setRequiredFilesFormat([]);
            clearSelectedFiles();
          }}
          closeOnUnmount
          className={cssClass}
          headerControls={
            <div className="mrg-left-40">
              <ButtonAddFolder onClick={handleOpenFolderModal()} />
              {shouldShowUploadView(displayMode) ? (
                <ButtonExploreFile onClick={toggleDisplayMode(ExplorerMode.EXPLORE)} />
              ) : (
                <ButtonUploadFile onClick={toggleDisplayMode(ExplorerMode.UPLOAD)} />
              )}
            </div>
          }
          {...footerOptions}
        >
          {shouldShowBulkActionsBar(isBulkActionsEnabled, selectedFiles?.length, displayMode) && (
            <BulkActionsBar
              selectedFiles={selectedFiles}
              onClearSelection={clearSelectedFiles}
              onMoveFiles={handleBulkMove}
              bulkAction={bulkAction}
              setBulkAction={setBulkAction}
              onDeleteFiles={handleBulkDelete}
            />
          )}
          <FileManagerFileDuplicateModal
            handleOnUpload={handleOnUpload}
            uploadableFiles={uploadableFiles}
            cancelHandler={closeOverwriteConfirmModal}
          />

          {isStoreInitializingInFileManager(store) ? (
            <div className={`${cssClass}__loader`}>
              <Loader size="lg" />
            </div>
          ) : (
            <>
              {shouldShowExplorerView(displayMode) && (
                <FileManagerExplorerView
                  store={store}
                  fileSelectable={fileSelectable}
                  previewableFile={previewableFile}
                  handleExplorerRowClick={handleExplorerRowClick}
                  openMoveModal={openMoveModal}
                  onFileSelected={onFileSelected}
                  onFileDelete={handleFileDelete}
                  onActiveFolderDelete={handleActiveFolderDelete}
                  requiredFilesFormat={requiredFilesFormat}
                />
              )}
              {shouldShowUploadView(displayMode) && (
                <FileManagerUpload
                  uploadableFiles={uploadableFiles}
                  onDeleteFile={handleDeleteFile}
                  onDestinationSelected={setDestinationFolder}
                  onDropFiles={setUploadableFiles}
                  onClearMissingData={handleClearMissingData}
                  fileSelectable={fileSelectable}
                />
              )}
            </>
          )}
        </Modal>
        <FileManagerMoveFileModal
          movableFile={determineMovableFile(previewableFile)}
          movableFiles={determineMovableFiles(isBulkActionsEnabled, selectedFiles?.length, selectedFiles, previewableFile)}
          onFileMoved={onBulkActionSuccess}
        />
      </>
    );
  }
);

export const FileManager = ({ confirmationModalName, ...rest }: FileManagerProps) => (
  <FileManagerContextProvider confirmationModalName={confirmationModalName}>
    <FileManagerFn {...rest} />
  </FileManagerContextProvider>
);

type ModalFooterButtonsProps = {
  missingData?: MissingData;
  onUpload: () => void;
  onCancel: () => void;
};
const ModalFooterButtons = ({ missingData, onUpload, onCancel }: ModalFooterButtonsProps) => (
  <div className={`${cssClass}__footer`}>
    <div className={`${cssClass}__footer--warning`}>
      {missingData ? <Message caption={missingDataWarning[missingData]} type="error" /> : null}
    </div>

    <div>
      <Button caption={`${clxDamTranslation}.upload`} onClick={onUpload} testId="upload" />
      <Button caption="generic.button.cancel" outline onClick={onCancel} />
    </div>
  </div>
);

type FolderActionProps = {
  onClick: () => void;
};
const ButtonAddFolder = ({ onClick }: FolderActionProps) => (
  <Button caption="modules.contentmanagement.addFolder" outline onClick={onClick} />
);
const ButtonUploadFile = ({ onClick }: FolderActionProps) => (
  <Button className="mrg-left-20" caption="modules.contentmanagement.uploadFiles" outline onClick={onClick} />
);

const ButtonExploreFile = ({ onClick }: FolderActionProps) => (
  <Button className="mrg-left-20" caption="modules.contentmanagement.useExistingFile" outline onClick={onClick} />
);
